import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useAsyncFn, useToggle } from 'react-use';
import { Link } from 'react-router-dom';

import { __t } from '@mssgme/shared';
import { ShoppingCartIcon } from '@mssgme/icons';
import { useNavigationUtils } from '@mssgme/hooks';
import { useCoreContext, useFrontAPI, useModalThemes, usePageData, useSavedOrders } from '../../hooks';
import { ThemedButton } from '../ThemedButton';
import { ThemedModal } from '../ThemedModal';

import styles from './BuyButton.scss';

const propTypes = {
    service: PropTypes.object.isRequired,
    interactive: PropTypes.bool,
    onCartAdd: PropTypes.func,
    productCurrency: PropTypes.string,
};

function BuyButton({ service, interactive, onCartAdd, productCurrency, ...rest }) {
    const api = useFrontAPI();
    const { generateUrl } = useNavigationUtils();
    const { router } = useCoreContext();
    const { currentOrder, saveOrderId } = useSavedOrders();
    const { _id: pageId } = usePageData();
    const { contentStyle } = useModalThemes();
    const isInCart = useMemo(() => !!currentOrder?.items.find((item) => item.productId === service._id), [
        currentOrder,
        service._id,
    ]);
    const orderId = currentOrder?._id;
    const orderPath = useMemo(() => router.orders.toView(orderId, generateUrl('.')), [orderId]);
    const isDifferentCurrency = useMemo(() => {
        const orderCurrency = currentOrder?.items[0]?.price?.currency;

        return !!(orderCurrency && orderCurrency !== productCurrency);
    }, [currentOrder?.items, productCurrency]);
    const [restrictCurrenciesMessage, toggleRestrictCurrenciesMessage] = useToggle(false);
    const [{ loading }, handleBuy] = useAsyncFn(async () => {
        if (isInCart || !interactive) {
            return;
        }

        // restrict adding products with different currencies
        if (isDifferentCurrency) {
            toggleRestrictCurrenciesMessage(true);

            return;
        }

        const payload = { items: [{ productId: service._id, quantity: 1 }] };
        let result;

        if (orderId) {
            result = await api.orders.modify({ orderId, payload });
        } else {
            result = await api.orders.create({ pageId, payload });

            saveOrderId(result._id);
        }

        onCartAdd && onCartAdd();

        return result;
    }, [interactive, service._id, pageId, orderId, isInCart, isDifferentCurrency, saveOrderId]);

    return (
        <>
            <ThemedButton
                fullWidth
                className={styles.root}
                theme={contentStyle}
                disabled={!interactive}
                isFetching={loading}
                beforeIcon={isInCart && <ShoppingCartIcon />}
                {...(isInCart
                    ? {
                        tag: Link,
                        to: {
                            pathname: orderPath,
                            state: service.slug ? { backPath: router.product.toView(service.slug) } : null,
                        },
                    }
                    : { onClick: handleBuy })}
                {...rest}
            >
                {isInCart ? __t('to_cart') : __t('buy')}
            </ThemedButton>
            {restrictCurrenciesMessage && (
                <ThemedModal
                    overlay
                    compact
                    padding
                    title={__t('error_occurred')}
                    onClose={toggleRestrictCurrenciesMessage}
                >
                    <div style={{ textAlign: 'center' }}>{__t('different_currencies_error')}</div>
                </ThemedModal>
            )}
        </>
    );
}

BuyButton.propTypes = propTypes;

export default BuyButton;
